// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useState } from 'react';
import { Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';
import HolePointsGrid, { resolveHoleGridColumns } from '@/features/tour-games/components/HolePointsGrid';
import type { HoleGridCell } from '@/features/tour-games/components/HolePointsGrid';
import { buildEclecticRows, buildRoundIndexById } from '@/features/tour-games/eclectic';
import type { EclecticSection } from '@/features/tour-games/types';
import { styles } from '../styles';

type Props = {
  section: EclecticSection | null;
  playerNameLookup: Map<string, string>;
};

// Eclectic standings: best Stableford points per hole across all tour rounds.
// Rows expand to a per-hole grid showing the best points and their source round.
export default function EclecticStandingsCard({ section, playerNameLookup }: Props) {
  const { t } = useI18n();
  const { width: viewportWidth } = useWindowDimensions();
  const [expandedPlayerId, setExpandedPlayerId] = useState<string | null>(null);
  // Screen (14px) and card (12px) padding on each side.
  const gridColumns = resolveHoleGridColumns(viewportWidth - 52);

  if (!section) return null;
  const rows = buildEclecticRows(section, playerNameLookup, t('tourLeaderboard.unknownPlayer'));
  if (rows.length === 0) return null;
  const roundIndexById = buildRoundIndexById(section.rounds);

  return (
    <View style={styles.summarySectionCard}>
      <Text style={styles.summarySectionTitle}>{t('tourLeaderboard.eclecticTitle')}</Text>
      <Text style={localStyles.subtitle}>{t('tourLeaderboard.eclecticSubtitle')}</Text>
      {rows.map((row) => {
        const expanded = expandedPlayerId === row.playerId;
        const cells: HoleGridCell[] = row.holes.map((hole) => ({
          holeNumber: hole.holeNumber,
          points: hole.bestRoundId ? hole.bestPoints : null,
          sublabel: hole.bestRoundId ? `R${roundIndexById.get(hole.bestRoundId) ?? '?'}` : undefined,
          state: 'plain',
        }));
        return (
          <View key={row.playerId}>
            <Pressable
              style={styles.summaryPlayerRow}
              onPress={() => setExpandedPlayerId(expanded ? null : row.playerId)}
              accessibilityRole="button"
              testID={`eclectic-row-${row.playerId}`}
            >
              <Text style={styles.summaryRank}>{`${row.position}.`}</Text>
              <View style={styles.summaryNameBlock}>
                <Text style={styles.summaryPlayerName} numberOfLines={1}>{row.name}</Text>
              </View>
              <Text style={styles.summaryMerits} testID={`eclectic-points-${row.playerId}`}>
                {t('tourLeaderboard.gamePoints', { points: row.totalPoints })}
              </Text>
            </Pressable>
            {expanded ? (
              <View style={localStyles.gridWrap}>
                <HolePointsGrid cells={cells} testIDPrefix={`eclectic-${row.playerId}`} columns={gridColumns} />
                <Text style={localStyles.gridHint}>{t('tourLeaderboard.eclecticGridHint')}</Text>
              </View>
            ) : null}
          </View>
        );
      })}
    </View>
  );
}

const localStyles = StyleSheet.create({
  subtitle: {
    fontSize: 12,
    color: appColors.text.muted,
    marginBottom: 8,
  },
  gridWrap: {
    paddingVertical: 8,
    backgroundColor: 'transparent',
  },
  gridHint: {
    fontSize: 11,
    color: appColors.text.muted,
    marginTop: 6,
  },
});
